import React, { useEffect, useRef, useState, useMemo } from "react";
import { useApi } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, CheckCircle2, AlertCircle, FilePlus2,
  ShieldCheck, MapPin, Wand2, Eye, ArrowRight,
} from "lucide-react";
import { fmtDateTime } from "@/lib/format";

const PHASE_LABELS = {
  engagement: "Phase 4 — Engagement",
  closing:    "Phase 5 — Closing",
  post_filing:"Phase 6 — Post-Filing",
};

export default function Forms() {
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Library</p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          Forms Library
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Upload your master agreements once, map each blank to a field, and ClaimWerks
          auto-fills a personalized DOCX + PDF per claimant every time you generate a claim package.
        </p>
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates" data-testid="forms-tab-templates">
            Master Templates
          </TabsTrigger>
          <TabsTrigger value="county" data-testid="forms-tab-county">
            County Claim Forms
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="pt-4">
          <TemplatesTab />
        </TabsContent>
        <TabsContent value="county" className="pt-4">
          <CountyTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- TEMPLATES TAB ---------------- */

function TemplatesTab() {
  const { get, upload, del, token } = useApi();
  const [slugs, setSlugs] = useState([]);
  const [items, setItems] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardTemplate, setWizardTemplate] = useState(null);

  const reload = async () => {
    const [s, t] = await Promise.all([
      get("/forms/templates/slugs"),
      get("/forms/templates"),
    ]);
    setSlugs(s.data || []);
    setItems(t.data || []);
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  const findFor = (slug) => items.find(i => i.slug === slug);

  const groupedSlugs = useMemo(() => {
    const g = { engagement: [], closing: [], post_filing: [] };
    for (const s of slugs) (g[s.phase] || g.closing).push(s);
    return g;
  }, [slugs]);

  const onUpload = async (slug, file, description) => {
    const fd = new FormData();
    fd.append("slug", slug);
    fd.append("description", description || "");
    fd.append("file", file);
    await upload("/forms/templates", fd);
    toast.success("Template uploaded");
    await reload();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this template? You can re-upload a new version.")) return;
    await del(`/forms/templates/${id}`);
    toast.success("Removed");
    reload();
  };

  const openWizard = (template) => {
    setWizardTemplate(template);
    setWizardOpen(true);
  };

  const downloadUrl = (id) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/forms/templates/${id}/download?auth=${token}`;

  return (
    <div className="space-y-6">
      {["engagement", "closing", "post_filing"].map(phase => (
        <div key={phase}>
          <div className="flex items-center gap-2 mb-3">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{PHASE_LABELS[phase]}</p>
            <div className="flex-1 border-t border-slate-200"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 cw-stagger">
            {groupedSlugs[phase].map((s) => {
              const item = findFor(s.slug);
              return (
                <Card key={s.slug} className="p-4 border-slate-200" data-testid={`template-slot-${s.slug}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
                        <h3 className="font-display text-base font-bold text-slate-900 truncate">{s.name}</h3>
                      </div>
                      <p className="text-[11px] font-mono text-slate-400 mt-0.5">{s.slug}</p>
                    </div>
                    {item ? (
                      item.underscore_mapping && Object.keys(item.underscore_mapping).length > 0 ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                          <CheckCircle2 className="h-3 w-3 mr-0.5" /> Mapped
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                          <AlertCircle className="h-3 w-3 mr-0.5" /> Needs mapping
                        </Badge>
                      )
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">
                        Missing
                      </Badge>
                    )}
                  </div>

                  {item ? (
                    <div className="space-y-2 mt-3">
                      <div className="p-2 bg-slate-50 border border-slate-200 rounded text-xs">
                        <p className="font-mono text-slate-900 truncate">{item.original_filename}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5 font-mono">
                          {item.blanks_count > 0 ? `${item.blanks_count} blanks · ` : ""}
                          {(item.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" onClick={() => openWizard(item)} data-testid={`template-map-${s.slug}`}
                          className="bg-blue-600 hover:bg-blue-700 text-white">
                          <Wand2 className="h-3.5 w-3.5 mr-1" /> Map fields
                        </Button>
                        <a href={downloadUrl(item.id)} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="outline" data-testid={`template-view-${s.slug}`}>View</Button>
                        </a>
                        <ReplaceUploader slug={s.slug} onUpload={onUpload} label="Replace" />
                        <Button size="sm" variant="ghost" onClick={() => onDelete(item.id)}
                          data-testid={`template-delete-${s.slug}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3">
                      <ReplaceUploader slug={s.slug} onUpload={onUpload} label="Upload template" primary />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {wizardTemplate && (
        <MappingWizard
          open={wizardOpen}
          template={wizardTemplate}
          onClose={() => { setWizardOpen(false); setWizardTemplate(null); reload(); }}
        />
      )}
    </div>
  );
}

/* ---------------- MAPPING WIZARD ---------------- */

function MappingWizard({ open, template, onClose }) {
  const { get, post, patch, token } = useApi();
  const [loading, setLoading] = useState(true);
  const [blanks, setBlanks] = useState([]);
  const [fields, setFields] = useState([]);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);

  // Each row of mapping[blank_id] is one of:
  //   {kind: "field", key: "claimant_name"}
  //   {kind: "literal", text: "..."}
  //   {kind: "skip"}
  // undefined = skip by default

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [analysis, fieldsResp] = await Promise.all([
          post(`/forms/templates/${template.id}/analyze`, {}),
          get(`/forms/autofill-fields`),
        ]);
        if (!alive) return;
        setBlanks(analysis.data.blanks || []);
        setFields(fieldsResp.data || []);
        setMapping(template.underscore_mapping || {});
      } catch (e) {
        toast.error("Could not analyze template");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, template?.id]); // eslint-disable-line

  const setRow = (id, value) => {
    setMapping(m => ({ ...m, [id]: value }));
  };
  const clearRow = (id) => {
    setMapping(m => { const c = { ...m }; delete c[id]; return c; });
  };

  const setKindForRow = (id, kind) => {
    if (kind === "skip") clearRow(id);
    else if (kind === "field") setRow(id, { kind: "field", key: "" });
    else if (kind === "literal") setRow(id, { kind: "literal", text: "" });
  };

  const save = async (alsoPreview = false) => {
    setBusy(true);
    try {
      // Drop empty rows
      const clean = {};
      for (const [k, v] of Object.entries(mapping)) {
        if (!v) continue;
        if (v.kind === "field" && !v.key) continue;
        if (v.kind === "literal" && !v.text?.trim()) continue;
        clean[k] = v;
      }
      await patch(`/forms/templates/${template.id}/mapping`, { underscore_mapping: clean });
      toast.success(`Mapped ${Object.keys(clean).length} of ${blanks.length} blanks`);
      if (alsoPreview) await preview();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const preview = async () => {
    // Trigger preview download
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/forms/templates/${template.id}/preview-fill`;
    try {
      const fd = new FormData();
      const r = await fetch(url, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Preview failed");
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `preview_${template.slug}.docx`;
      link.click();
    } catch (e) { toast.error("Preview failed"); }
  };

  const fieldsByGroup = useMemo(() => {
    const g = {};
    for (const f of fields) (g[f.group] = g[f.group] || []).push(f);
    return g;
  }, [fields]);

  const mappedCount = Object.values(mapping).filter(v => v && (
    (v.kind === "field" && v.key) || (v.kind === "literal" && v.text?.trim())
  )).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-blue-600" />
            <DialogTitle className="font-display">Map fields — {template.name}</DialogTitle>
          </div>
          <p className="text-xs text-slate-600 mt-1">
            Each blank in your template is shown with its surrounding context. Pick a field, type a fixed value,
            or leave it as &quot;Skip&quot; (the underscores will remain for signatures / notary lines).
            <span className="ml-2 font-mono text-slate-900">{mappedCount} / {blanks.length} mapped</span>
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-500">Analyzing template…</div>
        ) : blanks.length === 0 ? (
          <div className="py-10 text-center text-slate-500">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
            No underscore blanks detected. Your template might use named placeholders
            already (e.g. <code className="font-mono">{"{{claimant_name}}"}</code>) — those auto-fill without mapping.
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-slate-200">
                <tr>
                  <th className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 py-2 w-12">#</th>
                  <th className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 py-2">Context in template</th>
                  <th className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 py-2 w-32">Type</th>
                  <th className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 py-2 w-72">Value</th>
                </tr>
              </thead>
              <tbody>
                {blanks.map((b) => {
                  const cur = mapping[b.id];
                  const kind = !cur ? "skip" : cur.kind;
                  return (
                    <tr key={b.id} className="border-b border-slate-100 align-top" data-testid={`map-row-${b.id}`}>
                      <td className="py-2 font-mono text-xs font-bold text-slate-400">#{b.id}</td>
                      <td className="py-2 pr-3">
                        <div className="text-xs leading-relaxed">
                          <span className="text-slate-500">…{b.before}</span>
                          <span className="inline-block mx-1 px-1.5 py-0.5 bg-amber-100 border border-amber-300 rounded text-amber-900 font-mono">
                            ___{b.length}___
                          </span>
                          <span className="text-slate-500">{b.after}…</span>
                        </div>
                      </td>
                      <td className="py-2 pr-2">
                        <Select value={kind} onValueChange={(v) => setKindForRow(b.id, v)}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`map-kind-${b.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="field">Field</SelectItem>
                            <SelectItem value="literal">Fixed text</SelectItem>
                            <SelectItem value="skip">Skip (keep blank)</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2">
                        {kind === "field" && (
                          <Select value={cur?.key || ""} onValueChange={(v) => setRow(b.id, { kind: "field", key: v })}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`map-field-${b.id}`}>
                              <SelectValue placeholder="Pick a field…" />
                            </SelectTrigger>
                            <SelectContent className="max-h-80">
                              {Object.entries(fieldsByGroup).map(([g, fs]) => (
                                <div key={g}>
                                  <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{g}</div>
                                  {fs.map(f => (
                                    <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                                  ))}
                                </div>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {kind === "literal" && (
                          <Input
                            value={cur?.text || ""}
                            onChange={(e) => setRow(b.id, { kind: "literal", text: e.target.value })}
                            className="h-8 text-xs"
                            placeholder="Type the fixed text to insert"
                            data-testid={`map-literal-${b.id}`}
                          />
                        )}
                        {kind === "skip" && (
                          <span className="text-xs text-slate-400 italic">Underscores will remain (signature line)</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter className="border-t border-slate-200 pt-3 mt-2">
          <Button variant="outline" onClick={onClose} data-testid="wizard-cancel-btn">Cancel</Button>
          <Button variant="outline" onClick={preview} disabled={loading} data-testid="wizard-preview-btn">
            <Eye className="h-4 w-4 mr-1" /> Preview with sample contact
          </Button>
          <Button onClick={() => save(false)} disabled={busy || loading}
            className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="wizard-save-btn">
            {busy ? "Saving…" : (<>Save mapping <ArrowRight className="h-4 w-4 ml-1" /></>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- UPLOAD/REPLACE UTILITY ---------------- */

function ReplaceUploader({ slug, onUpload, label = "Upload", primary = false }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [desc, setDesc] = useState("");
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await onUpload(slug, file, desc);
      setOpen(false); setFile(null); setDesc("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"
          className={primary ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
          variant={primary ? "default" : "outline"}
          data-testid={`template-upload-btn-${slug}`}>
          <Upload className="h-3.5 w-3.5 mr-1" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Upload template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <input ref={ref} type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)}
            accept=".docx" data-testid={`template-file-input-${slug}`} />
          <div onClick={() => ref.current?.click()}
               className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center bg-slate-50 hover:bg-slate-100 cursor-pointer">
            <Upload className="h-6 w-6 text-slate-400 mx-auto mb-2" />
            {file ? (
              <>
                <div className="font-semibold text-sm">{file.name}</div>
                <div className="text-xs text-slate-500 font-mono">{(file.size / 1024).toFixed(1)} KB</div>
              </>
            ) : (
              <p className="text-sm text-slate-700">Click to select a DOCX</p>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Description (optional)</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="e.g., 'Standard 30% contingency, updated Jan 2026'"
              data-testid={`template-desc-input-${slug}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!file || busy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid={`template-submit-btn-${slug}`}>
            {busy ? "Uploading…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- COUNTY TAB (unchanged from v1) ---------------- */

function CountyTab() {
  const { get, upload, del, token } = useApi();
  const [items, setItems] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [open, setOpen] = useState(false);
  const [county, setCounty] = useState("");
  const [state, setState] = useState("");
  const [formName, setFormName] = useState("Surplus Funds Claim Form");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const reload = async () => {
    const [items, cov] = await Promise.all([
      get("/forms/county"),
      get("/forms/county/coverage"),
    ]);
    setItems(items.data || []);
    setCoverage(cov.data || []);
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  const submit = async () => {
    if (!file || !county || !state || !formName) {
      toast.error("County, state, form name, and file are required.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("county", county); fd.append("state", state);
      fd.append("form_name", formName); fd.append("notes", notes);
      fd.append("file", file);
      await upload("/forms/county", fd);
      toast.success("County form uploaded");
      setOpen(false);
      setCounty(""); setState(""); setNotes(""); setFile(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this county form?")) return;
    await del(`/forms/county/${id}`);
    toast.success("Deleted");
    reload();
  };

  const downloadUrl = (id) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/forms/county/${id}/download?auth=${token}`;

  const prefillFromCoverage = (c) => {
    setCounty(c.county); setState(c.state);
    setFormName("Surplus Funds Claim Form");
    setOpen(true);
  };

  const missing = coverage.filter(c => !c.covered);
  const present = coverage.filter(c => c.covered);

  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200 bg-amber-50/40">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-700" />
              <p className="text-xs font-bold uppercase tracking-widest text-amber-800">Coverage gaps</p>
            </div>
            <p className="text-sm text-slate-700 mt-1 max-w-xl">
              Counties present in your leads but missing a claim form. Click one to upload its form
              now — it&apos;ll auto-attach to every existing and future deal in that county.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="county-form-new-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
                <FilePlus2 className="h-4 w-4 mr-1.5" /> Add county form
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">Upload county claim form</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">County</Label>
                    <Input value={county} onChange={(e) => setCounty(e.target.value)}
                      placeholder="e.g., hillsborough" data-testid="county-form-county-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">State</Label>
                    <Input value={state} onChange={(e) => setState(e.target.value.toUpperCase())}
                      maxLength={2} placeholder="FL" data-testid="county-form-state-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Form name</Label>
                  <Input value={formName} onChange={(e) => setFormName(e.target.value)}
                    data-testid="county-form-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Notes (optional)</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Filing instructions, required attachments, mailing address, etc."
                    data-testid="county-form-notes-input" />
                </div>
                <input ref={ref} type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="county-form-file-input" />
                <div onClick={() => ref.current?.click()}
                     className="border-2 border-dashed border-slate-300 rounded-lg p-5 text-center bg-slate-50 hover:bg-slate-100 cursor-pointer">
                  <Upload className="h-5 w-5 text-slate-400 mx-auto mb-1" />
                  {file ? <div className="font-semibold text-sm">{file.name}</div> : <p className="text-sm text-slate-700">Click to select PDF/DOCX</p>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="county-form-submit-btn">
                  {busy ? "Uploading…" : "Upload"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {coverage.length === 0 ? (
          <p className="text-sm text-slate-500 mt-3">No leads yet — import a batch and gaps will show here.</p>
        ) : missing.length === 0 ? (
          <p className="text-sm text-emerald-700 font-semibold mt-3">
            <CheckCircle2 className="h-4 w-4 inline mr-1" />
            All {coverage.length} counties in your CRM are covered.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-3">
            {missing.map((c) => (
              <button key={`${c.state}-${c.county}`} onClick={() => prefillFromCoverage(c)}
                data-testid={`coverage-gap-${c.state}-${c.county}`}
                className="flex items-center gap-1.5 text-xs bg-white border border-amber-300 text-amber-800 rounded px-2 py-1 hover:bg-amber-100">
                <MapPin className="h-3 w-3" />
                {c.county.toUpperCase()}, {c.state}
                <span className="text-amber-600 font-mono">({c.leads})</span>
              </button>
            ))}
          </div>
        )}

        {present.length > 0 && (
          <div className="mt-3 pt-3 border-t border-amber-200">
            <p className="text-[11px] uppercase tracking-widest font-bold text-emerald-800 mb-1.5">Covered ({present.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {present.map((c) => (
                <span key={`${c.state}-${c.county}`} className="text-[11px] bg-white border border-emerald-300 text-emerald-700 rounded px-2 py-0.5">
                  {c.county}, {c.state}
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card className="border-slate-200 p-0 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p className="font-semibold text-slate-700">No county forms yet.</p>
            <p className="text-xs mt-1">Click &quot;Add county form&quot; to start your library.</p>
          </div>
        ) : (
          <table className="cw-table w-full">
            <thead>
              <tr><th>County · State</th><th>Form Name</th><th>File</th><th>Notes</th><th>Uploaded</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} data-testid={`county-form-row-${f.id}`}>
                  <td><span className="font-semibold">{f.county}, {f.state}</span></td>
                  <td className="font-semibold text-slate-900">{f.form_name}</td>
                  <td className="font-mono text-xs text-slate-600">{f.original_filename}</td>
                  <td className="text-xs text-slate-500 max-w-xs truncate">{f.notes || "—"}</td>
                  <td className="text-xs text-slate-500 font-mono">{fmtDateTime(f.created_at)}</td>
                  <td className="text-right">
                    <a href={downloadUrl(f.id)} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" data-testid={`county-form-view-${f.id}`}>View</Button>
                    </a>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(f.id)} data-testid={`county-form-delete-${f.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
